import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Instagram, ArrowUpRight, MessageCircle } from "lucide-react";

const TEL = "+36303915392";
const TEL_DISPLAY = "+36 30 391 5392";
const EMAIL = "ibihugo72@gmail.com";

const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 md:py-40 lg:py-48 bg-anthracite-950 overflow-hidden"
    >
      <div className="absolute top-20 right-0 font-serif text-[18rem] md:text-[26rem] text-white/[0.025] leading-none pointer-events-none select-none">
        05
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-20 md:mb-24"
        >
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-4 mb-6">
              <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">
                05 — Kapcsolat
              </span>
              <div className="w-16 h-px bg-gold-500/40" />
            </div>
            <h2
              data-testid="contact-title"
              className="font-serif text-cream leading-[1.02] tracking-[-0.02em] font-light"
            >
              <span className="block text-4xl md:text-5xl lg:text-6xl">Lépjen</span>
              <span className="block text-4xl md:text-5xl lg:text-6xl italic gold-gradient-text mt-2">
                kapcsolatba.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9">
            <p className="text-cream/55 font-light text-sm md:text-base leading-relaxed border-l border-gold-500/30 pl-6">
              A stúdió a Népstadiontól mindössze 2 perc sétára található. Diszkrét,
              nyugodt környezet.
            </p>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Contact details */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="space-y-1 border-y border-gold-500/15">
              <ContactRow
                icon={Phone}
                label="01 · Telefon"
                value={TEL_DISPLAY}
                href={`tel:${TEL}`}
                testid="contact-phone"
              />
              <ContactRow
                icon={MessageCircle}
                label="02 · SMS / Üzenet"
                value="Írjon, visszahívom"
                href={`sms:${TEL}`}
                testid="contact-sms"
              />
              <ContactRow
                icon={Mail}
                label="03 · E-mail"
                value={EMAIL}
                href={`mailto:${EMAIL}`}
                testid="contact-email"
              />
              <ContactRow
                icon={MapPin}
                label="04 · Stúdió"
                value="A Népstadiontól gyalog 2 percre"
                testid="contact-address"
              />
              <ContactRow
                icon={Clock}
                label="05 · Nyitvatartás"
                value={
                  <span className="block">
                    H–P · 09:00–20:00
                    <br />
                    <span className="text-cream/40 text-sm">Szombat · egyeztetés szerint</span>
                  </span>
                }
                testid="contact-hours"
              />
              <ContactRow
                icon={Instagram}
                label="06 · Instagram"
                value="@noblesse.massage"
                href="https://instagram.com/"
                testid="contact-instagram"
                external
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={`tel:${TEL}`}
                data-testid="contact-cta-call"
                className="cta-button group inline-flex items-center justify-between gap-4 bg-gold-500 hover:bg-gold-400 text-anthracite-950 px-8 py-5 text-xs uppercase tracking-[0.3em] font-medium transition-all flex-1"
              >
                <span>Hívás most</span>
                <Phone className="w-4 h-4 transition-transform group-hover:rotate-12" strokeWidth={1.8} />
              </a>
              <a
                href={`sms:${TEL}`}
                data-testid="contact-cta-sms"
                className="inline-flex items-center justify-between gap-4 border border-gold-500/40 hover:border-gold-500 text-cream hover:text-gold-500 px-8 py-5 text-xs uppercase tracking-[0.3em] font-medium transition-all flex-1"
              >
                <span>Üzenet</span>
                <MessageCircle className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 lg:col-span-7"
          >
            <div className="relative aspect-[4/3] lg:aspect-[5/6] border border-gold-500/20 overflow-hidden">
              <iframe
                title="NOBLESSE Stúdió helyszín"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Pusk%C3%A1s+Ar%C3%A9na+Budapest&output=embed"
                className="w-full h-full"
                style={{
                  border: 0,
                  filter: "invert(92%) hue-rotate(180deg) saturate(0.4) brightness(0.92) contrast(1.05)",
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              {/* Map overlay label */}
              <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-anthracite-950/85 backdrop-blur-xl border border-gold-500/20 p-4 sm:p-5 md:p-6">
                <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.35em] sm:tracking-[0.4em] text-gold-500 mb-1.5 sm:mb-2">
                  Helyszín
                </div>
                <div className="font-serif italic text-cream text-lg sm:text-xl md:text-2xl leading-tight">
                  Népstadion környéke · Budapest
                </div>
                <div className="text-[11px] sm:text-xs text-cream/50 font-light mt-1">
                  Pontos cím egyeztetés után
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ContactRow = ({ icon: Icon, label, value, href, testid, external }) => {
  const inner = (
    <div className="flex flex-row md:grid md:grid-cols-12 items-start md:items-center gap-4 py-5 md:py-6 border-b border-white/5 group">
      <div className="flex-shrink-0 md:col-span-1 pt-1 md:pt-0">
        <Icon className="w-4 h-4 text-gold-500" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0 md:col-span-10 md:grid md:grid-cols-10 md:items-center md:gap-4">
        <div className="md:col-span-4">
          <span className="text-[10px] uppercase tracking-[0.3em] md:tracking-[0.35em] text-cream/40">{label}</span>
        </div>
        <div className="md:col-span-6 text-cream/90 font-light text-sm sm:text-base md:text-lg mt-1 md:mt-0 break-words">
          {value}
        </div>
      </div>
      <div className="flex-shrink-0 md:col-span-1 md:text-right pt-1 md:pt-0">
        {href && (
          <ArrowUpRight
            className="w-4 h-4 text-cream/25 group-hover:text-gold-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all md:ml-auto"
            strokeWidth={1.5}
          />
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }
  return <div data-testid={testid}>{inner}</div>;
};

export default Contact;
